import { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchUserDetails } from "../actions/userprofile";
import { getUserId, getUserType } from "../helpers/common";
import { get, isEmpty, some } from "lodash";
import {
  USER_TYPE_AGENT,
  INVESTOR_MEMBER_PLAN,
  NOT_FREE_OR_MEMBER_PERIOD,
} from "../constants/common";

const PERIOD = {
  FREE: 1,
  MONTHLY: 2,
  ANNUAL: 3,
};

const PLAN_TYPE = {
  TRIAL: "Trial",
  LITE: "Lite",
  MEMBER: "Member",
  PRO_MEMBER: "Pro Member",
  INVESTOR_MEMBER: "Investor Member",
  PRO_PREFERRED: "Pro Preferred",
};

/**
 * @description This hook is used to get the user details
 * @param {number} reloadFlag - The reload flag
 * @returns {Object} The user details
 */
const useUserDetails = (reloadFlag = 0) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const userDetailsFromStore = useSelector(
    (state) => state.userProfile.userDetails,
  );
  const userDetails = get(userDetailsFromStore, 0);
  const userId = getUserId();
  const userType = get(userDetails, "UserTypeId", null);

  const loadUserData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await dispatch(fetchUserDetails()).unwrap();
    } catch (err) {
      setError(err.message || "Failed to fetch user details");
    } finally {
      setLoading(false);
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (userId) {
      loadUserData();
    }
  }, [userId, loadUserData, reloadFlag]);

  /**
   * @description This function is used to get the subscription period
   * @returns {number} The subscription period
   */
  const getSubscriptionPeriod = useCallback(() => {
    return get(userDetails, "period", null);
  }, [userDetails]);

  const isAgent = userType === USER_TYPE_AGENT;

  /**
   * @description This function is used to check if the plan is free
   * @returns {boolean} The free plan status
   */
  const isFreePlan = useMemo(() => {
    const period = getSubscriptionPeriod();
    if (!period) return true;

    return period === PERIOD.FREE;
  }, [getSubscriptionPeriod]);

  /**
   * @description This function is used to check if the user is a premium user
   * @returns {boolean} The premium user status
   */
  const isPremiumUser = useMemo(() => {
    if (isEmpty(userDetailsFromStore)) return false;

    return !isFreePlan && isAgent;
  }, [userDetailsFromStore, isFreePlan, isAgent]);

  /**
   * @description This function is used to get the current plan
   * @returns {string} The plan name
   */
  const getCurrentPlan = useCallback(() => {
    if (isEmpty(userDetailsFromStore)) return "";
    const firstUserPlan = get(userDetailsFromStore, [0, "PlanName"]);
    const secondUserPlan = get(userDetailsFromStore, [1, "PlanName"]);

    if (userDetailsFromStore.length > 1) {
      return firstUserPlan === PLAN_TYPE.TRIAL ? secondUserPlan : firstUserPlan;
    }

    return firstUserPlan || "";
  }, [userDetailsFromStore]);

  /**
   * @description This function is used to check if the user is on a trial period
   * @returns {boolean} The trial period status
   */
  const isTrialPeriod = useMemo(() => {
    return getCurrentPlan() === PLAN_TYPE.TRIAL;
  }, [getCurrentPlan]);

  return {
    userDetails,
    loading,
    error,
    isPremiumUser,
    isTrialPeriod,
    isFreePlan,
    isAgent,
    getCurrentPlan,
    getSubscriptionPeriod,
    loadUserData,
    isUserLoggedIn: !!userId,
  };
};

export default useUserDetails
